import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import useAuthStore from '@/stores/useAuthStore'
import { useModal } from '@/contexts/modal'
import { DataTableSelectionAction } from '@/components/ui/data-table-customized'

import { Tender } from '../types'
import {
    listNewTendersList,
    listFollowingTendersList,
    listUnfollowedTendersList,
    UpdateTendersStatusBulk,
    UpdateTenderPotential,
} from '../services'
import { TenderPotentialModalProps } from '@/modals/TenderPotentialModals'
import {
    TenderListVariant,
    getTenderListColumns,
    getTenderListActions,
    getTenderFilterToggles,
} from '../components/List/tender-list-config'

const VARIANT_CONFIG = {
    new: {
        queryKey: QUERY_KEYS.TENDERS,
        fetchFn: listNewTendersList,
    },
    following: {
        queryKey: QUERY_KEYS.TENDERS_FOLLOWED,
        fetchFn: listFollowingTendersList,
    },
    unfollowed: {
        queryKey: QUERY_KEYS.TENDERS_UNFOLLOWED,
        fetchFn: listUnfollowedTendersList,
    },
} as const

export const useTenderList = (variant: TenderListVariant) => {
    const { t } = useTranslation('tenders')
    const navigate = useNavigate()
    const { employee } = useAuthStore()
    const { setModal } = useModal()
    const config = VARIANT_CONFIG[variant]

    // URL params (no filters, only search)
    const { params, queryParams, setParams, searchInputProps } =
        useUrlSearchParams({
            defaults: { sort_by: 'id', sort_order: 'desc' },
        })

    // Build API params — inject employee_id for 'following' variant
    const apiParams = useMemo(() => {
        const base = { ...queryParams }
        if (variant === 'following' && employee?.employee_id) {
            return { ...base, employee_id: employee.employee_id }
        }
        return base
    }, [queryParams, variant, employee?.employee_id])

    // Data fetching
    const { data, renderState } = useQueryWithHandler({
        queryKey: [config.queryKey, apiParams],
        queryFn: () => config.fetchFn(apiParams),
        placeholderData: true,
    })

    // Selection state — reset when switching tabs
    const [selectedKeys, setSelectedKeys] = useState<string[]>([])

    useEffect(() => {
        setSelectedKeys([])
    }, [variant])

    // Bulk follow/unfollow mutation
    const { mutateAsync: bulkUpdateStatus } = useMutationWithHandler(
        {
            mutationFn: ({
                ids,
                is_followed,
            }: {
                ids: number[]
                is_followed: boolean
            }) => UpdateTendersStatusBulk(ids, is_followed),
        },
        {
            reactQueryKeys: [
                QUERY_KEYS.TENDERS,
                QUERY_KEYS.TENDERS_FOLLOWED,
                QUERY_KEYS.TENDERS_UNFOLLOWED,
            ],
            successMessage: t('update_follow_success'),
        }
    )

    const selectionActions: DataTableSelectionAction<Tender>[] = useMemo(() => {
        const actions: DataTableSelectionAction<Tender>[] = []

        // Show bulk-follow if not on "following" tab (items aren't followed yet)
        if (variant !== 'following') {
            actions.push({
                key: 'bulk-follow',
                label: t('bulk_follow'),
                variant: 'info',
                onClick: (rows) =>
                    bulkUpdateStatus({
                        ids: rows.map((r) => r.id),
                        is_followed: true,
                    }),
            })
        }

        // Show bulk-unfollow if not on "unfollowed" tab (items are followed)
        if (variant !== 'unfollowed') {
            actions.push({
                key: 'bulk-unfollow',
                label: t('bulk_unfollow'),
                variant: 'danger' as const,
                onClick: (rows) =>
                    bulkUpdateStatus({
                        ids: rows.map((r) => r.id),
                        is_followed: false,
                    }),
            })
        }

        return actions
    }, [t, bulkUpdateStatus, variant])

    // Potential change mutation (used only for 'following')
    const { mutateAsync: updatePotential } = useMutationWithHandler(
        {
            mutationFn: ({
                tender_id,
                is_potential,
                not_potential_reason,
            }: {
                tender_id: number
                is_potential: boolean
                not_potential_reason: string | null
            }) =>
                UpdateTenderPotential(
                    tender_id,
                    is_potential,
                    not_potential_reason
                ),
        },
        {
            reactQueryKeys: [QUERY_KEYS.TENDERS_FOLLOWED],
            successMessage: t('update_potential_success'),
        }
    )

    const handlePotentialChange = useCallback(
        (
            tender_id: number,
            is_potential: boolean,
            not_potential_reason: string | null
        ) => {
            if (!is_potential) {
                setModal<TenderPotentialModalProps>(
                    'TenderPotentialModals/index',
                    {
                        prevReason: not_potential_reason,
                        onResolve: (reason) => {
                            if (reason) {
                                updatePotential({
                                    tender_id,
                                    is_potential,
                                    not_potential_reason: reason,
                                })
                            }
                        },
                    }
                )
            } else {
                updatePotential({
                    tender_id,
                    is_potential,
                    not_potential_reason,
                })
            }
        },
        [setModal, updatePotential]
    )

    // Memoized configs
    const columns = useMemo(
        () =>
            getTenderListColumns(t, variant, {
                onPotentialChange:
                    variant === 'following' ? handlePotentialChange : undefined,
            }),
        [t, variant, handlePotentialChange]
    )

    const actions = useMemo(
        () =>
            getTenderListActions(t, {
                onViewDetails: (row) =>
                    row.detail_url &&
                    window.open(row.detail_url, '_blank', 'noopener'),
                onViewBidResult:
                    variant !== 'new'
                        ? (row) => navigate(`/tenders/${row.id}/bid-result`)
                        : undefined,
            }),
        [t, variant, navigate]
    )

    const filterToggles = useMemo(
        () => getTenderFilterToggles(t, variant),
        [t, variant]
    )

    // Handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )
    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        tenders: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),
        params,
        searchInputProps,
        columns,
        actions,
        filterToggles,
        selectedKeys,
        setSelectedKeys,
        selectionActions,
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        t,
    }
}
